import { LicenseMainFormController } from '../controller/LicenseMainFormController';
import { BaseWebComponentElement } from '../../../framework/BaseWebComponentElement';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { Observable } from 'rxjs';
import { FormGroupValue } from '../../../ui/gzForm/formGroup/types/form-group-value';
import { CompanyType } from '../../../company/types/company-type';
import { CompanyData } from '../../../company/types/company-data';
import { CompanyLicenseInformation } from '../../types/CompanyLicenseInformation';

export class LicenseMainFormWebComponent extends BaseWebComponentElement {
  public controller!: LicenseMainFormController;

  constructor() {
    super();
    this.initialSetup({
      controllerConstructor: LicenseMainFormController,
    });
  }

  public set company(companyData: CompanyData | null) {
    if (!companyData) {
      this.removeAttribute('data-company');
    }

    this.setAttribute('data-company', JSON.stringify(companyData));
  }

  public get company(): CompanyData | null {
    const companyDataString = this.getAttribute('data-company');

    if (!companyDataString) {
      return null;
    }

    return JSON.parse(companyDataString);
  }

  public set companyLicenseInformation(companyLicenseInformation: CompanyLicenseInformation | null) {
    if (!companyLicenseInformation) {
      this.removeAttribute('data-license-info');
    }

    this.setAttribute('data-license-info', JSON.stringify(companyLicenseInformation));
  }

  public get companyLicenseInformation(): CompanyLicenseInformation | null {
    const companyLicenseInformation = this.getAttribute('data-license-info');

    if (!companyLicenseInformation) {
      return null;
    }

    return JSON.parse(companyLicenseInformation);
  }

  public set context(newContext: CompanyActionContext) {
    this.setAttribute('context', newContext);
  }

  public get context(): CompanyActionContext {
    const context = this.getAttribute('context');

    if (!CompanyActionContext.isValid(context)) {
      throw new Error(`CompanyActionContext context not valid: ${context}`);
    }

    return context as CompanyActionContext;
  }

  public get editedCompanyId(): string | null {
    const editedCompanyId = this.company?._id ?? null;

    if (!editedCompanyId && this.context === CompanyActionContext.Edit) {
      throw new Error('EditedCompany record not provided in LicenseMainForm as an attribute.');
    }

    return editedCompanyId;
  }

  public setCompanyType(companyType: CompanyType) {
    this.controller.setCompanyType(companyType);
  }

  public setCanManageNetworks(canManageNetwork: boolean) {
    this.controller.view.setCanManageNetworks(canManageNetwork);
  }

  public setCanBeManagedByAbove(canBeManagedByAbove: boolean) {
    this.controller.view.setCanBeManagedFromAbove(canBeManagedByAbove);
  }

  public get valid$(): Observable<boolean> {
    return this.controller.valid$;
  }

  public get value(): FormGroupValue {
    return this.controller.getFormValue();
  }

  public getLicenseKeyValidity(): boolean {
    return this.controller.view.dom.subformComponents?.$prePaidSubForm?.getLicenseKeyValidity();
  }

  public getLicenseKeyFieldValue(): string {
    return this.controller.view.dom.subformComponents?.$prePaidSubForm?.getLicenseKeyFieldValue();
  }

  public isLicenseKeyEmpty(): boolean {
    return this.controller.view.dom.subformComponents?.$prePaidSubForm?.isLicenseKeyFieldEmpty();
  }

  public getActiveSubscriptionType() {
    return this.controller.getActiveSubscriptionType();
  }

  public resetPrepaidSubform() {
    const prePaidSubForm = this.controller.view.dom.subformComponents.$prePaidSubForm;

    if (prePaidSubForm.started) {
      prePaidSubForm.resetLicensePrePaidForm();
    }
  }

  public get hasUnsavedChanges(): boolean {
    return this.controller.view.formGroup.hasUnsavedChanges;
  }

  public get prepaidSubformHasIssues() {
    const prePaidSubForm = this.controller.view.dom.subformComponents.$prePaidSubForm;

    if (prePaidSubForm.started) {
      return prePaidSubForm.hasIssues;
    }

    return false;
  }
}
